import {RefObject, useMemo} from 'react';
import {WebcamRefTypes} from 'components/Webcam';
import {useFaceDetection, FacePosition} from './useFaceDetection';
import {useServerFaceDetection} from './useServerFaceDetection';
import {useLivenessModelsStore} from 'components/LivenessDetection/store';
import {canUseClientSideDetection} from '../utils/deviceCapabilities';

interface UseAdaptiveFaceDetectionProps {
  videoRef?: RefObject<WebcamRefTypes>;
  forceClientSide?: boolean; // Force client-side processing
  forceServerSide?: boolean; // Force server-side processing
  forceLivenessMechanism?: 'AUTO' | 'CLIENT' | 'SERVER';
}

export const useAdaptiveFaceDetection = ({
  videoRef,
  forceClientSide = false,
  forceServerSide = false,
  forceLivenessMechanism = 'AUTO',
}: UseAdaptiveFaceDetectionProps = {}) => {
  const setIsWebGLSupported = useLivenessModelsStore(state => state.setIsWebGLSupported);

  const finalForceClientSide = forceClientSide || forceLivenessMechanism === 'CLIENT';
  const finalForceServerSide = forceServerSide || forceLivenessMechanism === 'SERVER';

  const useClientDetection = useMemo(() => {
    return canUseClientSideDetection({
      forceClientSide: finalForceClientSide,
      forceServerSide: finalForceServerSide,
      setIsWebGLSupported,
    });
  }, [finalForceClientSide, finalForceServerSide, setIsWebGLSupported]);

  const clientDetection = useFaceDetection({
    videoRef,
    shouldPreload: useClientDetection,
  });

  const serverDetection = useServerFaceDetection({
    videoRef,
    shouldInitialize: !useClientDetection,
  });

  if (useClientDetection === null) {
    return {
      isModelLoading: true,
      facePosition: 'unknown' as FacePosition,
      startDetection: () => {},
      isCorrectPosition: () => false,
      isUsingServerDetection: null,
      isUsingClientDetection: undefined,
    };
  }

  return {
    isUsingClientDetection: useClientDetection,
    ...(useClientDetection ? clientDetection : serverDetection),
  };
};
